import { useCallback, useEffect, useMemo, useState } from "react";
import { flushSync } from "react-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import Box from "@mui/material/Box";
import Backdrop from "@mui/material/Backdrop";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import type { Dayjs } from "dayjs";
import type {
  GridPaginationModel,
  GridSortModel,
} from "@mui/x-data-grid-premium";
import AssessmentCenterImpactDataGrid from "./AssessmentCenterImpactDataGrid";
import { createManualImpactAssessmentColumns } from "./assessmentCenterColumns";
import ManualImpactAssessmentFilterBar from "./ManualImpactAssessmentFilterBar";
import type { AssessmentFilterOption } from "./assessmentFilterOption";
import {
  getManualAssessmentWorkflows,
  getManualWorkflowFilterOptions,
  manualWorkflowListSortParamFromSortModel,
  mapManualWorkflowToGridRow,
  buildSyncManualAssessmentWorkflowStatusParams,
  syncManualAssessmentWorkflowStatus,
  type GetManualWorkflowsParams,
  type SyncManualAssessmentWorkflowStatusParams,
  terminateManualAssessmentWorkflows,
  type ManualImpactAssessmentGridRow,
  type ManualWorkflowApiItem,
  type ManualWorkflowFilterOption,
} from "../../services/manualAssessmentService";
import { ShareManualIaPdfDialog } from "../RegulationDetails/ShareManualIaPdfDialog";
import { PDFViewer } from "../RegulationDetails/PDFViewer";
import { useManualIaPdfRowActions } from "./useManualIaPdfRowActions";
import { useHasPrivilege } from "../../hooks/useHasPrivilege";
import { MANUAL_IA_PDF_PRIVILEGES } from "../../services/permissionsService";
import { NAV_SOURCE_OMIT_DOMAIN } from "../../utils/externalNavigation";
import type { ManualIaPdfShareTarget } from "../../utils/manualAssessmentReportShare";
import {
  useAssessmentCenterPaginationHandlers,
  useClampAssessmentCenterPage,
  useStableServerRowCount,
} from "./useAssessmentCenterPagination";

function manualWorkflowFilterChoiceValue(
  o: ManualWorkflowFilterOption,
): string {
  const v = o.value;
  if (typeof v === "string" && v.trim()) return v.trim();
  return (o.name ?? "").trim();
}

function manualWorkflowNeedsStatusSync(item: ManualWorkflowApiItem): boolean {
  return (
    !item.process_name?.trim() &&
    !!item.regulation_id?.trim() &&
    !!item.regulation_version?.trim()
  );
}

function uniqueManualWorkflowStatusSyncTargets(
  items: ManualWorkflowApiItem[],
): SyncManualAssessmentWorkflowStatusParams[] {
  const seen = new Set<string>();
  return items.reduce<SyncManualAssessmentWorkflowStatusParams[]>((targets, item) => {
    if (!manualWorkflowNeedsStatusSync(item)) return targets;

    const params = buildSyncManualAssessmentWorkflowStatusParams(
      item.regulation_id,
      item.regulation_version,
    );
    if (!params) return targets;

    const key = `${params.regulation_id}::${params.regulation_version}`;
    if (seen.has(key)) return targets;

    seen.add(key);
    targets.push(params);
    return targets;
  }, []);
}

/**
 * Manual Impact Assessment tab — filter strip + grid (filters: Process Name, Status, Initiated By, Initiated Date).
 */
export default function ManualImpactAssessmentTable() {
  const queryClient = useQueryClient();
  const canManageManualAssessment = useHasPrivilege(
    "gri.assessment.manual.process.manage",
  );
  const canTerminateManualAssessment = useHasPrivilege(
    "gri.assessment.manual.process.terminate",
  );
  const canViewManualIaPdf = useHasPrivilege(MANUAL_IA_PDF_PRIVILEGES.view);
  const canDownloadManualIaPdf = useHasPrivilege(
    MANUAL_IA_PDF_PRIVILEGES.download,
  );
  const canShareManualIaPdf = useHasPrivilege(MANUAL_IA_PDF_PRIVILEGES.share);

  const [terminateDialog, setTerminateDialog] = useState<{
    open: boolean;
    workflowDbId: number;
    processName: string;
    rowId: string;
  }>({ open: false, workflowDbId: 0, processName: "", rowId: "" });

  const [terminatingRowId, setTerminatingRowId] = useState<string | null>(null);
  const [shareTarget, setShareTarget] = useState<ManualIaPdfShareTarget | null>(
    null,
  );
  const {
    previewPdf,
    downloadPdf,
    downloadingFilePath,
    pdfDialogOpen,
    selectedPdfUrl,
    closePdfDialog,
  } = useManualIaPdfRowActions();

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 20,
  });
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: "initiatedDate", sort: "desc" },
  ]);
  const { handleSortModelChange, handlePaginationModelChange } =
    useAssessmentCenterPaginationHandlers(setPaginationModel, setSortModel);

  const [filters, setFilters] = useState<{
    processName: string;
    status: string;
    initiatedBy: string;
    initiatedDate: Dayjs | null;
  }>({
    processName: "",
    status: "",
    initiatedBy: "",
    initiatedDate: null,
  });

  const [debouncedFilters, setDebouncedFilters] = useState(filters);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedFilters(filters), 350);
    return () => clearTimeout(t);
  }, [filters]);

  useEffect(() => {
    setPaginationModel((p) => ({ ...p, page: 0 }));
  }, [
    debouncedFilters.processName,
    debouncedFilters.status,
    debouncedFilters.initiatedBy,
    debouncedFilters.initiatedDate,
  ]);

  /** Flush debounced filters so refresh uses the same params as the visible filter bar. */
  const handleRefreshAssessmentList = useCallback(() => {
    flushSync(() => {
      setDebouncedFilters(filters);
    });
    void queryClient.invalidateQueries({
      queryKey: ["manual-assessment", "workflows"],
    });
  }, [filters, queryClient]);

  const sortParam = useMemo(
    () => manualWorkflowListSortParamFromSortModel(sortModel),
    [sortModel],
  );

  const { data, isLoading, isFetching } = useQuery({
    refetchOnWindowFocus: false,
    queryKey: [
      "manual-assessment",
      "workflows",
      debouncedFilters.processName,
      debouncedFilters.status,
      debouncedFilters.initiatedBy,
      debouncedFilters.initiatedDate?.toISOString() ?? "",
      paginationModel.page,
      paginationModel.pageSize,
      sortParam,
    ],
    queryFn: async () => {
      const workflowParams: GetManualWorkflowsParams = {
        page: paginationModel.page + 1,
        page_size: paginationModel.pageSize,
        process_name: debouncedFilters.processName || undefined,
        status: debouncedFilters.status || undefined,
        initiated_by: debouncedFilters.initiatedBy || undefined,
        initiated_date: debouncedFilters.initiatedDate
          ? debouncedFilters.initiatedDate.format("YYYY-MM-DD[T]00:00:00[Z]")
          : undefined,
        sort: sortParam,
      };

      const payload = await getManualAssessmentWorkflows(workflowParams);
      const syncTargets = uniqueManualWorkflowStatusSyncTargets(payload.items);

      if (syncTargets.length === 0) return payload;

      try {
        await Promise.all(
          syncTargets.map((target) => syncManualAssessmentWorkflowStatus(target)),
        );
      } catch (e) {
        console.warn(
          "Status update failed, proceeding with workflow list fetch",
          e,
        );
        return payload;
      }

      return getManualAssessmentWorkflows(workflowParams);
    },
  });

  const rows = useMemo(
    () => (data?.items ?? []).map((item) => mapManualWorkflowToGridRow(item)),
    [data?.items],
  );

  useClampAssessmentCenterPage(data?.total, setPaginationModel);
  const stableRowCount = useStableServerRowCount(data?.total);

  const { data: filterConfigs = [] } = useQuery({
    queryKey: ["manual-assessment", "workflow-filter-options"],
    queryFn: () => getManualWorkflowFilterOptions(),
    staleTime: 5 * 60 * 1000,
  });

  const statusOptions = useMemo((): AssessmentFilterOption[] => {
    const statusConfig = filterConfigs.find((f) => f.name === "status");
    return (statusConfig?.options ?? []).map((o) => ({
      label: o.name,
      value: manualWorkflowFilterChoiceValue(o),
    }));
  }, [filterConfigs]);

  const initiatedByOptions = useMemo((): AssessmentFilterOption[] => {
    const cfg = filterConfigs.find(
      (f) =>
        f.name === "initiated_by" ||
        f.name === "initiatedBy" ||
        f.name === "initiated_by_user",
    );
    return (cfg?.options ?? []).map((o) => ({
      label: o.name,
      value: manualWorkflowFilterChoiceValue(o),
    }));
  }, [filterConfigs]);

  const openTerminateDialog = useCallback((row: ManualImpactAssessmentGridRow) => {
    setTerminateDialog({
      open: true,
      workflowDbId: row.workflowDbId,
      processName: row.processName,
      rowId: row.id,
    });
  }, []);

  const openShareDialog = useCallback((row: ManualImpactAssessmentGridRow) => {
    if (!row.attachmentS3Uri) return;
    setShareTarget({
      regulationId: row.regulationId,
      regulationVersion: row.version,
      attachmentS3Uri: row.attachmentS3Uri,
      assessmentLink: row.assessmentLink,
      source: NAV_SOURCE_OMIT_DOMAIN.ASSESSMENT_CENTER,
    });
  }, []);

  const closeShareDialog = useCallback(() => {
    setShareTarget(null);
  }, []);

  const closeTerminateDialog = useCallback(() => {
    setTerminateDialog({
      open: false,
      workflowDbId: 0,
      processName: "",
      rowId: "",
    });
  }, []);

  const handleTerminateConfirm = async () => {
    const { workflowDbId, rowId } = terminateDialog;
    if (!workflowDbId && workflowDbId !== 0) return;
    closeTerminateDialog();

    try {
      setTerminatingRowId(rowId);
      const response = await terminateManualAssessmentWorkflows([workflowDbId]);
      if (response.status === "success") {
        setSnackbar({
          open: true,
          message: "Workflow terminated successfully.",
          severity: "success",
        });
        await queryClient.invalidateQueries({
          queryKey: ["manual-assessment", "workflows"],
        });
      } else {
        setSnackbar({
          open: true,
          message: response.message || "Failed to terminate workflow",
          severity: "error",
        });
      }
    } catch (err: unknown) {
      console.error("Error terminating workflow:", err);
      const msg =
        err instanceof Error ? err.message : "An error occurred while terminating the workflow";
      setSnackbar({ open: true, message: msg, severity: "error" });
    } finally {
      setTerminatingRowId(null);
    }
  };

  const manualColumns = useMemo(
    () =>
      createManualImpactAssessmentColumns({
        canManageProcess: canManageManualAssessment,
        canTerminate: canTerminateManualAssessment,
        terminatingRowId,
        downloadingFilePath,
        onRequestTerminate: openTerminateDialog,
        onRequestView: canViewManualIaPdf
          ? (row) => {
              void previewPdf(row.attachmentS3Uri);
            }
          : undefined,
        onRequestDownload: canDownloadManualIaPdf
          ? (row) => {
              void downloadPdf(row.attachmentS3Uri);
            }
          : undefined,
        onRequestShare: canShareManualIaPdf ? openShareDialog : undefined,
      }),
    [
      canManageManualAssessment,
      canTerminateManualAssessment,
      canViewManualIaPdf,
      canDownloadManualIaPdf,
      canShareManualIaPdf,
      terminatingRowId,
      downloadingFilePath,
      openTerminateDialog,
      openShareDialog,
      previewPdf,
      downloadPdf,
    ],
  );

  const showStrongLoading = isFetching || terminatingRowId !== null;

  return (
    <>
      <Backdrop
        open={showStrongLoading}
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          color: "#fff",
          position: "absolute",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <CircularProgress color="inherit" size={24} />
          <Typography sx={{ fontWeight: 600 }}>
            {terminatingRowId ? "Terminating…" : "Loading…"}
          </Typography>
        </Box>
      </Backdrop>
      <Box
        sx={{
          backgroundColor: "#fff",
          pt: 2,
          pb: 2,
          position: "relative",
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
      <ManualImpactAssessmentFilterBar
        onRefresh={handleRefreshAssessmentList}
        processName={filters.processName}
        onProcessNameChange={(next) =>
          setFilters((p) => ({ ...p, processName: next }))
        }
        status={filters.status}
        statusOptions={statusOptions}
        onStatusChange={(next) => setFilters((p) => ({ ...p, status: next }))}
        initiatedBy={filters.initiatedBy}
        initiatedByOptions={initiatedByOptions}
        onInitiatedByChange={(next) =>
          setFilters((p) => ({ ...p, initiatedBy: next }))
        }
        initiatedDate={filters.initiatedDate}
        onInitiatedDateChange={(next) =>
          setFilters((p) => ({ ...p, initiatedDate: next }))
        }
      />
      <Box
        sx={{
          mt: 2,
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <AssessmentCenterImpactDataGrid
          embedded
          fillHeight
          ariaLabel="Manual impact assessment list"
          columns={manualColumns}
          rows={rows}
          loading={isLoading || isFetching}
          rowCount={stableRowCount}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationModelChange}
          sortingMode="server"
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
        />
      </Box>
      </Box>

      <Dialog
        open={terminateDialog.open}
        onClose={(_event, reason) => {
          if (reason !== "backdropClick") {
            closeTerminateDialog();
          }
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: "12px" } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography component="span" variant="h6" sx={{ fontWeight: 600, fontSize: "1.1rem" }}>
            Terminate This Manual Assessment Process?
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 1, pb: 2 }}>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Are you sure you want to terminate the Manual Assessment process &quot;
            <strong>{terminateDialog.processName}</strong>&quot;? Once terminated, it cannot be
            reactivated.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="outlined" onClick={closeTerminateDialog}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleTerminateConfirm}
            sx={{ textTransform: "none" }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <PDFViewer
        open={pdfDialogOpen}
        pdfUrl={selectedPdfUrl}
        title="PDF Preview"
        onClose={closePdfDialog}
      />

      {shareTarget ? (
        <ShareManualIaPdfDialog
          open
          variant="assessmentCenter"
          target={shareTarget}
          onClose={closeShareDialog}
        />
      ) : null}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
